import {BaseComponent} from '../base/component.base.js';
import {escapeHtml} from '../../core/escape-html.js';
import {formatDate, formatEuro, statusPresentation, toLocalIsoDate} from '../../core/billing/billing.core.js';
import {clientInvoiceGroups, renewalText} from '../../core/billing/client-invoice.core.js';
import {oneOffIncomePerYear} from '../../core/billing/report.core.js';
import {BILLING_VIEW} from '../../core/vocabulary.js';
import '../billing-charts/billing-charts.component.js';
import '../billing-totals/billing-totals.component.js';
import '../platform-costs/platform-costs.component.js';
import '../billing-add-item/billing-add-item.component.js';
import template from './billing-overview.template.html';
import styles from './billing-overview.styles.css';

/** @typedef {import('../../shell/types/billing.types.js').BillingItemApiResponse} BillingItem */
/** @typedef {import('../../shell/types/billing.types.js').BillingSummaryApiResponse} BillingSummary */

/** The "due soon" window shown in the Due empty state while the server's configured value is unknown. */
const DEFAULT_DUE_SOON_DAYS = 60;

/**
 * Billing overview page. Its To invoice tab shows one block per client (all active items of live clients;
 * the Due toggle keeps the clients with an item that is due) and emits `billing-client-invoice-request`
 * ({group}) when a block's "Invoice client" button is pressed. The "Add item" button emits
 * `billing-add-request`; the driving adapter answers by opening the `addItem` form with the clients.
 */
export class BillingOverviewComponent extends BaseComponent {
    /** Gives "today"; replaceable so tests do not depend on the clock. @type {() => Date} */
    today = () => new Date();
    #dueOnly = true;
    /** @type {BillingItem[]} */
    #items = [];
    /** The blocks on screen, by index. */
    #groups = [];
    #dueSoonDays = DEFAULT_DUE_SOON_DAYS;
    #report;
    #selectedYear;

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.$('#filter-due').addEventListener('click', () => this.#select(true));
        this.$('#filter-all').addEventListener('click', () => this.#select(false));
        this.$('#btn-add-item').addEventListener('click', () => this.emit('billing-add-request'));
        this.shadowRoot.addEventListener('click', (e) => this.#handleClick(e));
        for (const button of this.$$('.view-tab')) button.addEventListener('click', () => this.#view(button.dataset.view));
        this.$('[role="tablist"]').addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const view = event.key === 'Home' ? BILLING_VIEW.RESULTS : event.key === 'End' ? BILLING_VIEW.TO_INVOICE
                : event.target.dataset.view === BILLING_VIEW.RESULTS ? BILLING_VIEW.TO_INVOICE : BILLING_VIEW.RESULTS;
            this.#view(view);
            this.$(`[data-view="${view}"]`).focus();
        });
        this.addEventListener('billing-year-select', (event) => {
            this.#selectedYear = event.detail.year;
            this.#showReport();
        });
        this.addEventListener('billing-manage-costs', () => {
            this.#view(BILLING_VIEW.TO_INVOICE);
            this.$('#cost-management').open = true;
            this.platformCosts.showAll = true;
            this.emit('platform-costs-range', {showAll: true});
            this.$('#cost-management summary').focus();
            this.$('#cost-management').scrollIntoView({block: 'start'});
        });
        this.items = [];
    }

    #select(dueOnly) {
        if (dueOnly !== this.#dueOnly) this.dueOnly = dueOnly;
    }

    #handleClick(e) {
        const button = e.target.closest('.btn-invoice-client');
        if (button) {
            this.emit('billing-client-invoice-request', {group: this.#groups[Number(button.dataset.index)]});
        }
    }

    /** @param {boolean} value whether only the clients with an item that is due are listed */
    set dueOnly(value) {
        this.#dueOnly = value;
        for (const [id, active] of [['#filter-due', value], ['#filter-all', !value]]) {
            const button = this.$(id);
            button.classList.toggle('active', active);
            button.setAttribute('aria-pressed', String(active));
        }
        this.items = this.#items;
    }

    get dueOnly() {
        return this.#dueOnly;
    }

    /** @param {boolean} value */
    set loading(value) {
        const overlay = this.$('#loading-overlay');
        if (overlay) {
            overlay.hidden = !value;
        }
    }

    /** @returns {import('../billing-add-item/billing-add-item.component.js').BillingAddItemComponent} the "Add item" form this page hosts */
    get addItem() {
        return this.$('#add-item');
    }

    /** @returns {HTMLElement} the platform costs section this page hosts */
    get platformCosts() {
        return this.$('#platform-costs');
    }

    #view(view) {
        for (const button of this.$$('.view-tab')) {
            const selected = button.dataset.view === view;
            button.setAttribute('aria-selected', String(selected));
            button.tabIndex = selected ? 0 : -1;
            this.$(`#${button.getAttribute('aria-controls')}`).hidden = !selected;
        }
    }

    /** @param {import('../../shell/types/billing.types.js').BillingReportApiResponse} report */
    set report(report) {
        this.#report = report;
        if (this.#selectedYear === undefined) this.#selectedYear = report.year;
        this.#showReport();
    }

    #showReport() {
        if (!this.#report) return;
        this.$('#charts').showYears(this.#report.years, this.#selectedYear, oneOffIncomePerYear(this.#report.oneOffIncomes));
        this.$('#totals').showReport(this.#report, this.#selectedYear);
    }

    showReportUnavailable() {
        this.#report = undefined;
        this.$('#charts').showUnavailable();
        this.$('#totals').showUnavailable();
    }

    set summary(summary) { this.#setDueSoonDays(summary.dueSoonDays); }
    showSummaryUnavailable() { this.#setDueSoonDays(DEFAULT_DUE_SOON_DAYS); }

    #setDueSoonDays(days) {
        if (days === this.#dueSoonDays) return;
        this.#dueSoonDays = days;
        if (this.#groups.length === 0) this.items = this.#items;
    }

    /** @param {BillingItem[]} items all active items of live clients */
    set items(items) {
        const container = this.$('#billing-content');
        this.#items = items;
        this.#groups = clientInvoiceGroups(items, this.#dueOnly, toLocalIsoDate(this.today()));

        if (this.#groups.length === 0) {
            container.innerHTML = '';
            const empty = document.createElement('empty-state');
            const title = document.createElement('span');
            title.slot = 'title';
            title.textContent = this.#dueOnly ? `Nothing to invoice in the next ${this.#dueSoonDays} days` : 'No billing items yet';
            empty.appendChild(title);
            empty.appendChild(document.createTextNode(this.#dueOnly
                ? 'Choose All clients to see every client, including the ones that are up to date.'
                : "Add a recurring item on a client's page."));
            container.appendChild(empty);
            return;
        }

        const blocks = this.#groups.map((group, index) => this.#block(group, index)).join('');
        const total = this.#groups.reduce((sum, group) => sum + group.proposal.totalCents, 0);
        container.innerHTML = this.#dueOnly
            ? `${blocks}<p class="invoice-total">Total to invoice (excl. VAT) <span id="total-due">${escapeHtml(formatEuro(total / 100))}</span></p>`
            : blocks;
    }

    #block(group, index) {
        const status = statusPresentation(group.status);
        const renewal = `Renews on ${renewalText(group.renewal)}${group.suggested ? ' (suggested)' : ''}`;
        const {proposal} = group;
        const covers = proposal.coversUntil ? `Covers until ${formatDate(proposal.coversUntil)}. ` : '';
        return `
            <section class="client-block" aria-labelledby="client-${index}">
                <div class="client-block-header">
                    <div class="client-block-title">
                        <h3 id="client-${index}"><a class="client-link" href="#/clients/${encodeURIComponent(group.slug)}">${escapeHtml(group.name)}</a></h3>
                        <span class="badge ${status.badgeClass}">${escapeHtml(status.label)}</span>
                        <span class="client-block-meta">${covers}${renewal}.</span>
                    </div>
                    <span class="client-block-total">${escapeHtml(formatEuro(proposal.totalCents / 100))}</span>
                    <button type="button" class="btn btn-primary btn-sm btn-invoice-client" data-index="${index}"
                        aria-label="Invoice ${escapeHtml(group.name)}">
                        <i aria-hidden="true" class="fa-solid fa-file-invoice"></i> Invoice client
                    </button>
                </div>
                <table class="item-rows">
                    <thead class="visually-hidden">
                        <tr><th scope="col">Item</th><th scope="col">Status</th><th scope="col">Period</th><th scope="col">Amount excl. VAT</th></tr>
                    </thead>
                    <tbody>
                    ${proposal.lines.map((line) => this.#row(line.item, `${formatDate(line.item.paidUntil)} to ${formatDate(line.periodTo)}${line.bridge ? ' <span class="badge badge-gray">Bridge</span>' : ''}`, formatEuro(line.amount))).join('')}
                    ${proposal.later.map((item) => this.#row(item, `<span class="later">Paid until ${formatDate(item.paidUntil)}, not on this invoice</span>`, '')).join('')}
                    </tbody>
                </table>
            </section>`;
    }

    /** @param {string} period trusted markup */
    #row(item, period, amount) {
        const status = statusPresentation(item.status);
        return `
            <tr>
                <th scope="row">${escapeHtml(item.label)}</th>
                <td class="nowrap"><span class="badge ${status.badgeClass}">${escapeHtml(status.label)}</span></td>
                <td>${period}</td>
                <td class="num">${escapeHtml(amount)}</td>
            </tr>`;
    }
}

customElements.define('billing-overview', BillingOverviewComponent);
